import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Timer, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

/**
 * iter164 — Subscriber Auto-Purge status widget.
 * Shows retention window, last run, next run, and eligible-now count.
 * Provides a "Purge now" button that bypasses the 24h throttle.
 * Only surfaced inside the Subscribers "Deleted" view.
 */
export default function AutoPurgeStatus({ onPurged }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/subscribers/purge-status');
      setStatus(r.data);
    } catch {
      toast.error('Failed to load auto-purge status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePurgeNow = async () => {
    if (!confirm(`Run auto-purge now?\n\nThis will PERMANENTLY delete every soft-deleted user older than ${status?.retention_days || 90} days along with all of their data. Continue?`)) return;
    try {
      setPurging(true);
      const r = await api.post('/admin/subscribers/purge-now');
      if (r.data.purged > 0) {
        toast.success(`Auto-purge complete: ${r.data.purged} user(s) permanently deleted`);
        onPurged?.();
      } else {
        toast(`No eligible users (retention window ${r.data.retention_days} days)`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-purge failed');
    } finally {
      setPurging(false);
    }
  };

  const rel = (iso) => {
    if (!iso) return '—';
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
      return '—';
    }
  };

  return (
    <Card className="mb-4 border-slate-700/60 bg-slate-900/40" data-testid="auto-purge-status-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-100">
              <Timer className="h-4 w-4 text-slate-400" />
              Auto-Purge Schedule
            </CardTitle>
            <CardDescription className="mt-1">
              Soft-deleted users are permanently purged after{' '}
              <strong className="text-slate-200">{status?.retention_days ?? 90} days</strong>.
              Runs once every 24 hours.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="auto-purge-refresh"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handlePurgeNow}
              disabled={purging || loading || (status?.eligible_now ?? 0) === 0}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="auto-purge-run-now"
            >
              {purging ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Trash2 className="h-3 w-3 mr-2" />}
              Purge now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Eligible now</div>
            <div className="mt-1">
              {(status?.eligible_now ?? 0) > 0 ? (
                <Badge className="bg-amber-900/40 text-amber-200 border border-amber-500/30" data-testid="auto-purge-eligible-count">
                  {status.eligible_now}
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-slate-800 text-slate-400" data-testid="auto-purge-eligible-count">
                  0
                </Badge>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Last run</div>
            <div className="mt-1 text-slate-200" data-testid="auto-purge-last-run">{rel(status?.last_run)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Last purged</div>
            <div className="mt-1 text-slate-200" data-testid="auto-purge-last-count">
              {status?.last_count ?? 0} user{(status?.last_count ?? 0) === 1 ? '' : 's'}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Next scheduled</div>
            <div className="mt-1 text-slate-200" data-testid="auto-purge-next-run">{rel(status?.next_run)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
